/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  Eye,
  Image as ImageIcon,
  Monitor,
  Pencil,
  Redo2,
  Save,
  Smartphone,
  Undo2,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useHideDashboardChrome } from '../../contexts/DashboardChromeContext';
import { useWebsiteDataFile, type DataFileName } from '../../hooks/useWebsiteDataFile';
import { useSitePreview, type PreviewElement } from '../../hooks/useSitePreview';
import { derivePatches, hasLivePreview, type BusinessContext } from './editors/previewPatches';
import {
  getEditableFieldByKey,
  isSafeButtonDestination,
  type EditableFieldMatch,
} from './editors/editableFieldMap';
import { getValueAtPath, setValueAtPath } from './editors/dotPath';
import { validateRequiredVariablePreservation } from './editors/templateTokens';
import DeploymentStatusPill from './editors/DeploymentStatusPill';
import { isPendingDeployment, type Deployment } from './editors/deploymentStatus';
import BusinessEditor from './editors/BusinessEditor';
import ServicesEditor from './editors/ServicesEditor';
import AreasEditor from './editors/AreasEditor';
import ContentEditor from './editors/ContentEditor';
import FaqsEditor from './editors/FaqsEditor';
import TestimonialsEditor from './editors/TestimonialsEditor';
import PostsEditor from './editors/PostsEditor';
import ThemeEditor from './editors/ThemeEditor';
import ImageUpload from './editors/ImageUpload';

interface Section {
  label: string;
  hint: string;
  file: DataFileName;
  scrollKey?: string;
}

const SECTIONS: Section[] = [
  { label: 'Business info', hint: 'Name, phone, address, hours', file: 'business', scrollKey: 'contact' },
  { label: 'Top banner', hint: 'The big headline visitors see first', file: 'content', scrollKey: 'hero' },
  { label: 'Services', hint: 'What you offer', file: 'services', scrollKey: 'services' },
  { label: 'Service areas', hint: 'Towns and neighborhoods you cover', file: 'areas', scrollKey: 'area-map' },
  { label: 'Reviews', hint: 'Customer testimonials', file: 'testimonials', scrollKey: 'testimonials' },
  { label: 'FAQs', hint: 'Common questions and answers', file: 'faqs', scrollKey: 'faq' },
  { label: 'Blog', hint: 'Articles and posts', file: 'posts', scrollKey: 'blog' },
  { label: 'Colors and look', hint: 'Palette, fonts, and style', file: 'theme' },
];

type DraftMap = Partial<Record<DataFileName, any>>;
type ShaMap = Partial<Record<DataFileName, string>>;

type DraftOperation = {
  file: DataFileName;
  before: any;
  after: any;
};

type InlineEdit = {
  element: PreviewElement;
  field: EditableFieldMatch;
  originalValue: string;
  value: string;
  error?: string;
};

interface Props {
  websiteId: string;
  domain?: string;
  previewUrl?: string;
  onClose: () => void;
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function cleanPlainText(value: string, maxLength = 2_000): string {
  const normalized = value.replace(/\r\n?/g, '\n');
  return Array.from(normalized)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code === 9 || code === 10 || code >= 32 && code !== 127;
    })
    .join('')
    .slice(0, maxLength);
}

const WebsiteEditor: React.FC<Props> = ({
  websiteId,
  domain,
  previewUrl: previewUrlOverride,
  onClose,
}) => {
  const queryClient = useQueryClient();
  // The editor owns the whole viewport — drop the dashboard nav while it is open.
  useHideDashboardChrome();
  const [activeFile, setActiveFile] = useState<DataFileName>('content');
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [baselines, setBaselines] = useState<DraftMap>({});
  const [shas, setShas] = useState<ShaMap>({});
  const [history, setHistory] = useState<{ past: DraftOperation[]; future: DraftOperation[] }>({
    past: [],
    future: [],
  });
  const [selectedElement, setSelectedElement] = useState<PreviewElement | null>(null);
  const [pendingEdit, setPendingEdit] = useState<{ element: PreviewElement; field: EditableFieldMatch } | null>(null);
  const [inlineEdit, setInlineEdit] = useState<InlineEdit | null>(null);
  const [buttonHrefDraft, setButtonHrefDraft] = useState('');
  const [buttonHrefError, setButtonHrefError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [panelVisible, setPanelVisible] = useState(true);
  const [closeWarning, setCloseWarning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [deploymentLoading, setDeploymentLoading] = useState(true);
  const [deploymentRefreshPending, setDeploymentRefreshPending] = useState(false);
  const deployPollCountRef = useRef(0);
  const draftsRef = useRef<DraftMap>({});
  const historyRef = useRef(history);
  const focusScrollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inlineInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const activeSection = useMemo(
    () => SECTIONS.find((section) => section.file === activeFile) ?? SECTIONS[0],
    [activeFile],
  );

  const { data, sha, isLoading, error, reload } = useWebsiteDataFile(websiteId, activeFile);
  const { data: businessData } = useWebsiteDataFile(websiteId, 'business');
  const { data: servicesData } = useWebsiteDataFile(websiteId, 'services');

  const previewBaseUrl = previewUrlOverride || (domain ? `https://${domain}/` : null);

  const goToFileForElement = useCallback((element: PreviewElement) => {
    const field = getEditableFieldByKey(element.key);
    if (field) {
      setActiveFile(field.file);
      return field;
    }
    const section = SECTIONS.find((candidate) => candidate.scrollKey === element.section);
    if (section) setActiveFile(section.file);
    return null;
  }, []);

  const handleElementSelected = useCallback((element: PreviewElement) => {
    setSelectedElement(element);
    goToFileForElement(element);
  }, [goToFileForElement]);

  const handleEditRequested = useCallback((element: PreviewElement) => {
    setSelectedElement(element);
    const field = goToFileForElement(element);
    if (!field || !field.direct || element.kind !== 'text') {
      setInlineEdit(null);
      setToast({ message: 'Use the structured controls for this element.', type: 'info' });
      return;
    }
    setPendingEdit({ element, field });
  }, [goToFileForElement]);

  const handleSectionClick = useCallback((key: string) => {
    const match = SECTIONS.find((section) => section.scrollKey === key);
    if (match) setActiveFile(match.file);
  }, []);

  const preview = useSitePreview({
    previewUrl: previewBaseUrl,
    onSectionClick: handleSectionClick,
    onElementSelected: handleElementSelected,
    onEditRequested: handleEditRequested,
  });

  const businessDraft = drafts.business || businessData;
  const businessCtx: BusinessContext = useMemo(() => ({
    businessName: businessDraft?.name,
    city: businessDraft?.address?.city,
    state: businessDraft?.address?.state,
    region: businessDraft?.address?.state,
    phone: businessDraft?.phone,
    phoneRaw: businessDraft?.phoneRaw,
    address: businessDraft?.address?.full,
    email: businessDraft?.email,
    rating: businessDraft?.rating != null ? String(businessDraft.rating) : undefined,
    reviewCount: businessDraft?.reviewCount != null ? String(businessDraft.reviewCount) : undefined,
  }), [businessDraft]);

  const serviceNames = useMemo(() => {
    const source = drafts.services || servicesData;
    return Array.isArray(source?.services)
      ? source.services.map((service: any) => service.name).filter(Boolean)
      : [];
  }, [drafts.services, servicesData]);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    if (!data) return;
    setDrafts((current) => {
      if (current[activeFile] !== undefined) return current;
      const next = { ...current, [activeFile]: data };
      draftsRef.current = next;
      return next;
    });
    setBaselines((current) => current[activeFile] === undefined
      ? { ...current, [activeFile]: data }
      : current);
    if (sha) setShas((current) => ({ ...current, [activeFile]: sha }));
  }, [activeFile, data, sha]);

  const formData = drafts[activeFile] ?? data ?? null;
  const selectedField = selectedElement ? getEditableFieldByKey(selectedElement.key) : null;
  const selectedImage = selectedElement?.kind === 'src' && selectedField
    ? {
        file: selectedField.file,
        path: selectedField.path,
        uploadPath: selectedField.file === 'content' ? 'hero' : selectedField.file,
        label: selectedField.label,
      }
    : null;
  const selectedButtonPath = selectedElement?.key.startsWith('cta.button') ? 'ctaBanner.buttonHref' : null;
  const previewReady = preview.ready;
  const sendPreviewContent = preview.sendContent;
  const sendPreviewTheme = preview.sendTheme;

  useEffect(() => {
    if (!previewReady || !formData) return;
    if (activeFile === 'theme') sendPreviewTheme(formData);
    else sendPreviewContent(derivePatches(activeFile, formData, businessCtx));
  }, [activeFile, businessCtx, formData, previewReady, sendPreviewContent, sendPreviewTheme]);

  useEffect(() => {
    if (!selectedElement || !formData) return;
    const parts = selectedElement.key.split('.');
    if (!['services', 'testimonials', 'faqs', 'posts'].includes(parts[0]) || !/^\d+$/.test(parts[1] || '')) return;
    const editorKey = `${parts[0]}.${parts[1]}`;
    const frame = requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(`[data-editor-key="${editorKey}"]`);
      if (target?.dataset.editorExpanded === 'false') {
        target.querySelector<HTMLButtonElement>(':scope > button')?.click();
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeFile, formData, selectedElement]);

  useEffect(() => {
    if (!selectedButtonPath || !formData) return;
    setButtonHrefDraft(getValueAtPath<string>(formData, selectedButtonPath) || '');
    setButtonHrefError(null);
  }, [formData, selectedButtonPath]);

  useEffect(() => {
    if (!pendingEdit || pendingEdit.field.file !== activeFile || !formData) return;
    const rawValue = getValueAtPath(formData, pendingEdit.field.path);
    if (typeof rawValue !== 'string') {
      setPendingEdit(null);
      setToast({ message: 'This value is available in the structured panel.', type: 'info' });
      return;
    }
    setInlineEdit({
      ...pendingEdit,
      originalValue: rawValue,
      value: rawValue,
    });
    setPendingEdit(null);
  }, [activeFile, formData, pendingEdit]);

  const inlineEditKey = inlineEdit?.field.editableKey;
  useEffect(() => {
    if (!inlineEditKey) return;
    const frame = requestAnimationFrame(() => {
      inlineInputRef.current?.focus();
      inlineInputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [inlineEditKey]);

  const dirtyFiles = useMemo(
    () => (Object.keys(drafts) as DataFileName[]).filter(
      (file) => baselines[file] !== undefined && !sameValue(drafts[file], baselines[file]),
    ),
    [baselines, drafts],
  );
  const hasUnsavedChanges = dirtyFiles.length > 0;

  const sendDraftPreview = useCallback((file: DataFileName, next: any, immediate = false) => {
    const send = () => {
      if (file === 'theme') preview.sendTheme(next);
      else preview.sendContent(derivePatches(file, next, businessCtx));
    };
    if (immediate) {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      send();
      return;
    }
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(send, 120);
  }, [businessCtx, preview]);

  const applyDraft = useCallback((file: DataFileName, next: any, record = true) => {
    const before = draftsRef.current[file];
    if (before === undefined || sameValue(before, next)) return;
    const nextDrafts = { ...draftsRef.current, [file]: next };
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
    if (record) {
      const nextHistory = {
        past: [...historyRef.current.past, { file, before, after: next }].slice(-100),
        future: [],
      };
      historyRef.current = nextHistory;
      setHistory(nextHistory);
    }
    sendDraftPreview(file, next, true);
  }, [sendDraftPreview]);

  const handleChange = useCallback((next: any) => {
    applyDraft(activeFile, next);
  }, [activeFile, applyDraft]);

  const handleUndo = useCallback(() => {
    const operation = historyRef.current.past.at(-1);
    if (!operation) return;
    const nextHistory = {
      past: historyRef.current.past.slice(0, -1),
      future: [operation, ...historyRef.current.future],
    };
    historyRef.current = nextHistory;
    setHistory(nextHistory);
    setActiveFile(operation.file);
    setInlineEdit(null);
    applyDraft(operation.file, operation.before, false);
  }, [applyDraft]);

  const handleRedo = useCallback(() => {
    const operation = historyRef.current.future[0];
    if (!operation) return;
    const nextHistory = {
      past: [...historyRef.current.past, operation],
      future: historyRef.current.future.slice(1),
    };
    historyRef.current = nextHistory;
    setHistory(nextHistory);
    setActiveFile(operation.file);
    setInlineEdit(null);
    applyDraft(operation.file, operation.after, false);
  }, [applyDraft]);

  const cancelInlineEdit = useCallback(() => {
    setInlineEdit(null);
  }, []);

  const commitInlineEdit = useCallback(() => {
    if (!inlineEdit) return;
    const value = cleanPlainText(inlineEdit.value, inlineEdit.field.maxLength);
    if (!value.trim()) {
      setInlineEdit({ ...inlineEdit, error: 'Text cannot be empty.' });
      return;
    }
    const tokenCheck = validateRequiredVariablePreservation(inlineEdit.originalValue, value);
    if (!tokenCheck.valid) {
      setInlineEdit({
        ...inlineEdit,
        error: `Keep ${tokenCheck.missingTokens.map((token) => `{{${token}}}`).join(', ')} in this text.`,
      });
      return;
    }
    const currentDraft = draftsRef.current[inlineEdit.field.file];
    if (!currentDraft) return;
    const next = setValueAtPath(currentDraft, inlineEdit.field.path, value);
    applyDraft(inlineEdit.field.file, next);
    setInlineEdit(null);
  }, [applyDraft, inlineEdit]);

  const saveButtonDestination = useCallback(() => {
    if (!selectedButtonPath || !formData) return;
    const value = buttonHrefDraft.trim();
    if (!isSafeButtonDestination(value)) {
      setButtonHrefError('Use a site path such as /contact or a valid https, mailto, or tel URL.');
      return;
    }
    applyDraft(activeFile, setValueAtPath(formData, selectedButtonPath, value));
    setButtonHrefError(null);
  }, [activeFile, applyDraft, buttonHrefDraft, formData, selectedButtonPath]);

  const refreshDeployment = useCallback(async () => {
    try {
      const res = await api.get(`/api/websites/${websiteId}/deployment`);
      setDeployment(res.data?.deployment ?? null);
      setDeploymentRefreshPending(false);
    } catch {
      // Leave the last known status; the pill falls back to "unavailable".
    } finally {
      setDeploymentLoading(false);
    }
  }, [websiteId]);

  const handlePublish = useCallback(async () => {
    const files = (Object.keys(draftsRef.current) as DataFileName[]).filter(
      (file) => baselines[file] !== undefined && !sameValue(draftsRef.current[file], baselines[file]),
    );
    if (!files.length) return;
    const missingSha = files.find((file) => !shas[file]);
    if (missingSha) {
      setToast({ message: `Reload ${missingSha} before publishing.`, type: 'error' });
      return;
    }

    setPublishing(true);
    try {
      const result = await api.post(`/api/websites/${websiteId}/content/publish`, {
        files: files.map((file) => ({
          file,
          data: draftsRef.current[file],
          sha: shas[file],
        })),
      });
      const publishedDrafts = { ...draftsRef.current };
      setBaselines((current) => {
        const next = { ...current };
        files.forEach((file) => { next[file] = publishedDrafts[file]; });
        return next;
      });
      const nextHistory = { past: [], future: [] };
      historyRef.current = nextHistory;
      setHistory(nextHistory);
      await Promise.all(files.map((file) => queryClient.invalidateQueries({
        queryKey: ['website-content', websiteId, file],
      })));
      setToast({
        message: result.data.deploymentStatus === 'local'
          ? 'Changes published to the local showcase.'
          : 'Changes committed. Live deployment status is not available yet.',
        type: 'success',
      });
      // Reflect the freshly created deployment record and (re)start bounded polling.
      deployPollCountRef.current = 0;
      setDeploymentRefreshPending(true);
      void refreshDeployment();
    } catch (publishError: any) {
      const status = publishError.response?.status;
      const message = status === 409
        ? 'This site changed elsewhere. Reload before publishing to avoid overwriting it.'
        : status === 422
          ? 'One or more changes are invalid. Review the highlighted content and try again.'
        : publishError.response?.data?.error || publishError.message || 'Could not publish changes.';
      setToast({ message, type: 'error' });
    } finally {
      setPublishing(false);
    }
  }, [baselines, queryClient, refreshDeployment, shas, websiteId]);

  // Initial deployment status load.
  useEffect(() => {
    void refreshDeployment();
  }, [refreshDeployment]);

  // Bounded polling while a deployment is still in progress. No fixed timer
  // pretends completion — we re-read the real record, and stop after a cap so a
  // pipeline that never reports back (current infra) doesn't poll forever.
  useEffect(() => {
    if (!deploymentRefreshPending && !isPendingDeployment(deployment)) return;
    if (deployPollCountRef.current >= 20) return;
    const timer = setTimeout(() => {
      deployPollCountRef.current += 1;
      void refreshDeployment();
    }, 6_000);
    return () => clearTimeout(timer);
  }, [deployment, deploymentRefreshPending, refreshDeployment]);

  const requestClose = useCallback(() => {
    if (hasUnsavedChanges) setCloseWarning(true);
    else onClose();
  }, [hasUnsavedChanges, onClose]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey;
      if (modifier && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) handleRedo();
        else handleUndo();
      } else if (modifier && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void handlePublish();
      } else if (event.key === 'Escape') {
        if (closeWarning) setCloseWarning(false);
        else if (inlineEdit) cancelInlineEdit();
        else requestClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      if (focusScrollRef.current) clearTimeout(focusScrollRef.current);
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [cancelInlineEdit, closeWarning, handlePublish, handleRedo, handleUndo, inlineEdit, requestClose]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 7000 : 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const goToSection = useCallback((file: DataFileName, scrollKey?: string) => {
    setActiveFile(file);
    setInlineEdit(null);
    setSelectedElement(null);
    preview.clearSelection();
    if (scrollKey) preview.scrollTo(scrollKey);
  }, [preview]);

  const handlePanelFocus = useCallback((_event: FocusEvent<HTMLDivElement>) => {
    if (!activeSection.scrollKey) return;
    if (focusScrollRef.current) clearTimeout(focusScrollRef.current);
    focusScrollRef.current = setTimeout(() => preview.scrollTo(activeSection.scrollKey!), 150);
  }, [activeSection.scrollKey, preview]);

  const renderEditor = () => {
    if (error) {
      return (
        <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{error}</p>
          <button onClick={reload} className="mt-2 font-semibold underline">Retry</button>
        </div>
      );
    }
    if (isLoading || !formData) {
      return <div className="mx-auto mt-16 h-7 w-7 animate-spin rounded-full border-2 border-accent border-t-transparent" />;
    }
    const props = { data: formData, onChange: handleChange };
    switch (activeFile) {
      case 'business': return <BusinessEditor {...props} />;
      case 'services': return <ServicesEditor {...props} websiteId={websiteId} domain={domain} />;
      case 'areas': return <AreasEditor {...props} />;
      case 'content': return <ContentEditor {...props} websiteId={websiteId} domain={domain} />;
      case 'faqs': return <FaqsEditor {...props} />;
      case 'testimonials': return <TestimonialsEditor {...props} serviceNames={serviceNames} />;
      case 'posts': return <PostsEditor {...props} websiteId={websiteId} domain={domain} />;
      case 'theme': return <ThemeEditor {...props} />;
      default: return null;
    }
  };

  const canvasWidth = viewport === 'mobile' ? 390 : Math.max(640, window.innerWidth - (panelVisible ? 430 : 0));
  const inlineStyle: React.CSSProperties | undefined = inlineEdit ? {
    left: inlineEdit.element.rect.left + 420 > canvasWidth ? 12 : Math.max(12, inlineEdit.element.rect.left),
    right: inlineEdit.element.rect.left + 420 > canvasWidth ? 12 : undefined,
    top: inlineEdit.element.rect.bottom + 190 > window.innerHeight
      ? Math.max(12, inlineEdit.element.rect.top - 12)
      : inlineEdit.element.rect.bottom + 10,
    transform: inlineEdit.element.rect.bottom + 190 > window.innerHeight ? 'translateY(-100%)' : undefined,
  } : undefined;

  // Rendered into <body>: the dashboard wraps page content in a `relative z-[1]`
  // container, which is its own stacking context — an overlay nested inside it can
  // never paint above the z-50 sidebar no matter how high its own z-index goes.
  return createPortal(
    <AnimatePresence>
      <motion.div
        key="editor-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex bg-zinc-950/45"
      >
        <div className="flex h-full w-full flex-col bg-white">
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-3 sm:px-4">
            <div className="flex min-w-0 items-center gap-3">
              <h1 className="whitespace-nowrap font-display text-base font-semibold text-primary">Website editor</h1>
              {domain && <span className="hidden truncate font-mono text-xs text-muted sm:inline">{domain}</span>}
              {hasUnsavedChanges && (
                <span className="hidden text-xs font-medium text-amber-700 sm:inline" aria-live="polite">
                  {dirtyFiles.length} draft {dirtyFiles.length === 1 ? 'file' : 'files'}
                </span>
              )}
              <DeploymentStatusPill deployment={deployment} loading={deploymentLoading} />
            </div>

            <div className="hidden items-center gap-1 lg:flex">
              <button title="Undo" aria-label="Undo" onClick={handleUndo} disabled={!history.past.length} className="rounded p-2 text-zinc-600 hover:bg-zinc-100 disabled:opacity-30"><Undo2 size={17} /></button>
              <button title="Redo" aria-label="Redo" onClick={handleRedo} disabled={!history.future.length} className="rounded p-2 text-zinc-600 hover:bg-zinc-100 disabled:opacity-30"><Redo2 size={17} /></button>
              <span className="mx-1 h-5 w-px bg-zinc-200" />
              <button title="Desktop viewport" aria-label="Desktop viewport" onClick={() => setViewport('desktop')} className={`rounded p-2 ${viewport === 'desktop' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}><Monitor size={17} /></button>
              <button title="Mobile viewport" aria-label="Mobile viewport" onClick={() => setViewport('mobile')} className={`rounded p-2 ${viewport === 'mobile' ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}><Smartphone size={17} /></button>
              <button title={panelVisible ? 'Hide properties' : 'Show properties'} aria-label={panelVisible ? 'Hide properties' : 'Show properties'} onClick={() => setPanelVisible((visible) => !visible)} className="ml-1 rounded p-2 text-zinc-600 hover:bg-zinc-100"><Eye size={17} /></button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => void handlePublish()}
                disabled={publishing || !hasUnsavedChanges}
                aria-label="Publish changes"
                title="Publish changes"
                className="inline-flex h-9 items-center gap-2 rounded bg-cyan-700 px-3 text-sm font-semibold text-white hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {publishing ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Save size={16} />}
                <span className="hidden sm:inline">Publish</span>
              </button>
              <button onClick={requestClose} className="rounded p-2 text-zinc-600 hover:bg-zinc-100" aria-label="Close editor" title="Close editor"><X size={19} /></button>
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            <div className="relative hidden min-w-0 flex-1 items-stretch justify-center overflow-hidden bg-zinc-200 p-3 lg:flex">
              {preview.previewSrc ? (
                <div
                  className="relative h-full overflow-hidden bg-white shadow-[0_8px_30px_rgba(0,0,0,.12)] transition-[width] duration-200"
                  style={{ width: viewport === 'mobile' ? 390 : '100%', maxWidth: '100%' }}
                >
                  <iframe
                    ref={preview.iframeRef}
                    src={preview.previewSrc}
                    title="Editable website preview"
                    className="h-full w-full border-0"
                    onLoad={preview.handleIframeLoad}
                  />

                  {!preview.ready && (
                    <div className="pointer-events-none absolute bottom-3 left-3 border border-zinc-200 bg-white/95 px-2.5 py-1.5 text-xs text-zinc-600 shadow-sm">
                      Connecting preview...
                    </div>
                  )}

                  {preview.ready && preview.protocol === 'legacy' && (
                    <div className="pointer-events-none absolute bottom-3 left-3 border border-amber-200 bg-amber-50/95 px-2.5 py-1.5 text-xs text-amber-800 shadow-sm">
                      Legacy preview: use the property panel to edit.
                    </div>
                  )}

                  <AnimatePresence>
                    {inlineEdit && preview.protocol === 'v1' && (
                      <motion.form
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        onSubmit={(event) => { event.preventDefault(); commitInlineEdit(); }}
                        className="absolute z-20 w-[min(420px,calc(100%-24px))] border border-cyan-700 bg-white p-3 shadow-xl"
                        style={inlineStyle}
                        aria-label={`Edit ${inlineEdit.field.label}`}
                      >
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-zinc-700">
                          <Pencil size={14} className="text-cyan-700" />
                          {inlineEdit.field.label}
                        </div>
                        {inlineEdit.field.multiline ? (
                          <textarea
                            ref={(node) => { inlineInputRef.current = node; }}
                            rows={4}
                            value={inlineEdit.value}
                            maxLength={inlineEdit.field.maxLength}
                            onChange={(event) => setInlineEdit({ ...inlineEdit, value: event.target.value, error: undefined })}
                            className="w-full resize-none border border-zinc-300 px-2.5 py-2 text-sm leading-5 text-zinc-900 outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-700/15"
                          />
                        ) : (
                          <input
                            ref={(node) => { inlineInputRef.current = node; }}
                            value={inlineEdit.value}
                            maxLength={inlineEdit.field.maxLength}
                            onChange={(event) => setInlineEdit({ ...inlineEdit, value: event.target.value, error: undefined })}
                            className="w-full border border-zinc-300 px-2.5 py-2 text-sm text-zinc-900 outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-700/15"
                          />
                        )}
                        {inlineEdit.originalValue.includes('{{') && (
                          <p className="mt-1.5 text-xs text-zinc-500">Keep variable tokens such as {'{{businessName}}'} intact.</p>
                        )}
                        {inlineEdit.error && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{inlineEdit.error}</p>}
                        <div className="mt-3 flex justify-end gap-2">
                          <button type="button" onClick={cancelInlineEdit} className="inline-flex items-center gap-1.5 rounded border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"><X size={14} />Cancel</button>
                          <button type="submit" className="inline-flex items-center gap-1.5 rounded bg-cyan-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-800"><Check size={14} />Save</button>
                        </div>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex items-center text-sm text-zinc-500">Live preview is available after a site has a domain.</div>
              )}
            </div>

            {panelVisible && (
              <aside className="flex min-h-0 w-full shrink-0 flex-col border-l border-zinc-200 bg-white lg:w-[430px]">
                <div className="shrink-0 overflow-x-auto border-b border-zinc-200 px-3 py-2">
                  <div className="flex gap-1">
                    {SECTIONS.map((section) => (
                      <button
                        key={section.file}
                        onClick={() => goToSection(section.file, section.scrollKey)}
                        title={section.hint}
                        className={`whitespace-nowrap rounded px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                          section.file === activeFile
                            ? 'bg-zinc-900 text-white'
                            : 'text-zinc-600 hover:bg-zinc-100'
                        }`}
                      >
                        {section.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4" onFocus={handlePanelFocus}>
                  {selectedElement && (
                    <div className="mb-4 flex items-start gap-2 border-l-2 border-cyan-700 bg-cyan-50 px-3 py-2 text-xs text-cyan-950">
                      <Pencil size={14} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold">Selected: {selectedField?.label || selectedElement.label}</p>
                        <p className="mt-0.5 font-mono text-[11px] opacity-70">{selectedElement.key}</p>
                      </div>
                    </div>
                  )}
                  {selectedImage && selectedImage.file === activeFile && formData && (
                    <div className="mb-4 border border-zinc-200 bg-zinc-50 p-3">
                      <div className="mb-2 flex items-center gap-2">
                        <ImageIcon size={15} className="text-cyan-700" />
                        <p className="text-sm font-semibold text-primary">{selectedImage.label}</p>
                      </div>
                      <p className="mb-3 text-xs text-muted">Replace the selected photo. The new reference remains a draft until Publish.</p>
                      <ImageUpload
                        websiteId={websiteId}
                        currentUrl={getValueAtPath<string>(formData, selectedImage.path)}
                        path={selectedImage.uploadPath}
                        siteBaseUrl={previewBaseUrl || undefined}
                        aspectRatio="aspect-[16/9]"
                        onUploaded={(url) => applyDraft(activeFile, setValueAtPath(formData, selectedImage.path, url))}
                        onRemoved={() => applyDraft(activeFile, setValueAtPath(formData, selectedImage.path, ''))}
                      />
                    </div>
                  )}
                  {selectedButtonPath && formData && (
                    <div className="mb-4 border border-zinc-200 bg-zinc-50 p-3">
                      <p className="text-sm font-semibold text-primary">Button destination</p>
                      <p className="mt-1 text-xs text-muted">Leave blank to keep the estimate form, or enter a site path such as /contact.</p>
                      <input
                        value={buttonHrefDraft}
                        onChange={(event) => { setButtonHrefDraft(event.target.value); setButtonHrefError(null); }}
                        placeholder="/contact"
                        aria-label="Button destination"
                        className="mt-3 w-full border border-zinc-300 bg-white px-2.5 py-2 text-sm text-zinc-900 outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-700/15"
                      />
                      {buttonHrefError && <p className="mt-1.5 text-xs font-medium text-red-600" role="alert">{buttonHrefError}</p>}
                      <div className="mt-3 flex justify-end gap-2">
                        <button type="button" onClick={() => setButtonHrefDraft(getValueAtPath<string>(formData, selectedButtonPath) || '')} className="rounded border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700">Cancel</button>
                        <button type="button" onClick={saveButtonDestination} className="rounded bg-cyan-700 px-3 py-1.5 text-xs font-semibold text-white">Save destination</button>
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-xs text-muted">{activeSection.hint}</p>
                    {!hasLivePreview(activeFile) && (
                      <p className="mt-1 text-xs text-amber-600">Changes here appear after publishing.</p>
                    )}
                  </div>
                  {renderEditor()}
                </div>
              </aside>
            )}
          </div>

          <AnimatePresence>
            {toast && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className={`fixed bottom-4 left-1/2 z-[140] -translate-x-1/2 border px-4 py-2.5 text-sm font-medium shadow-lg ${
                  toast.type === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : toast.type === 'error'
                      ? 'border-red-200 bg-red-50 text-red-800'
                      : 'border-zinc-200 bg-white text-zinc-700'
                }`}
                role="status"
              >
                {toast.message}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {closeWarning && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[150] flex items-center justify-center bg-zinc-950/55 p-4">
                <motion.div initial={{ scale: 0.98, y: 8 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-md border border-zinc-200 bg-white p-5 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="unsaved-title">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={21} />
                    <div>
                      <h2 id="unsaved-title" className="font-display text-lg font-semibold text-zinc-950">Leave with unpublished changes?</h2>
                      <p className="mt-1 text-sm leading-6 text-zinc-600">Your draft changes will be lost if you close the editor now.</p>
                    </div>
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <button onClick={() => setCloseWarning(false)} className="rounded border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50">Keep editing</button>
                    <button onClick={onClose} className="rounded bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800">Discard and close</button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default WebsiteEditor;
